/**
 * One visit, posted to `/user/history/lookup/`.
 *
 * Apart from `lookup-history.ts`, which is a 词典 entry or a 例句 page saying
 * the reader arrived on it. This is the request that page makes, and the word
 * segmenter makes the same one when a reader opens a meaning in its list. The
 * two pages decide what counts as a look-up and share what a look-up costs.
 *
 * The handler is `src/user/history-lookup.ts`. It checks the session, the
 * origin and the canonical object behind the identifier, and a reader who has
 * not turned history on is answered without anything being written.
 * `docs/lookup-history.md` has the whole protocol.
 */

/** Which kind of thing was looked up. */
export type LookupKind = "ci" | "liju";

/**
 * The statuses worth posting again.
 *
 * 409 is expiry reconciliation running, 429 is the rate limit and the three
 * 5xx are the table. Every other status is an answer, including the 404 a
 * word with no published entry gets.
 */
const retryable = [409, 429, 500, 502, 504];

/** How many times one visit is posted before it is given up on. */
const attempts = 4;

/**
 * How long after a visit began a retry is still worth making.
 *
 * The handler refuses a body whose `startedAt` is more than 60 seconds old, so
 * a fourth attempt starting at 50 has time to arrive.
 */
const windowSeconds = 50;

/**
 * Record one visit, and say nothing about how it went.
 *
 * **Every attempt carries the same body.** The visit identifier and the
 * instant the visit began are settled here and reused, so four attempts are
 * one visit to the handler rather than four.
 *
 * **A failure is swallowed.** Nothing a reader can see depends on this, and a
 * page whose history request failed has to go on working. The handler's own
 * answer is ignored for the same reason. A reader with collection paused is
 * told `paused` and there is nothing to do about it here.
 * @param fetched - What makes the request, which a test hands its own.
 * @param visit - What was looked up.
 */
export function recordLookup(
  fetched: typeof fetch,
  visit: { kind: LookupKind; id: string },
): void {
  const startedAt = Math.floor(Date.now() / 1000);
  const body = new URLSearchParams({
    kind: visit.kind,
    id: visit.id,
    visitId: globalThis.crypto.randomUUID(),
    startedAt: String(startedAt),
  });

  const send = async (attempt: number): Promise<void> => {
    try {
      const response = await fetched("/user/history/lookup/", {
        method: "POST",
        credentials: "same-origin",
        body,
        cache: "no-store",
      });

      if (response.ok || !retryable.includes(response.status)) {
        return;
      }
    } catch {
      /* A failed history request leaves the page usable. */
    }

    if (
      attempt < attempts - 1 &&
      Date.now() / 1000 < startedAt + windowSeconds
    ) {
      globalThis.setTimeout(
        () => {
          void send(attempt + 1);
        },
        1000 * 2 ** attempt,
      );
    }
  };

  void send(0);
}
